import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Standard React class-based error boundary. Catches render-time errors
 * anywhere in the subtree and shows a friendly panel with the message
 * plus a "Reload" button so the user can recover without opening
 * DevTools. Async errors (promises, event handlers) do not reach here
 * and are handled inline by their callers.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="loading-screen">
          <h2>Something went wrong</h2>
          <div className="status-missing">
            {this.state.error.message || String(this.state.error)}
          </div>
          <p className="muted small">
            The UI crashed. Reload to recover. If this keeps happening,
            check the browser console for details.
          </p>
          <button className="btn-primary" onClick={this.handleReload}>
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
